import { isEmpty, isEqual, isNil, isString } from 'lodash';
import { useMemo } from 'react';
import { useWorkflowEntitiesByIds } from '@shared/scenes/workflows/hooks';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';
import { getEntityTitle } from '../getEntityTitle';
import { EditableField, Field } from './Field';

export interface RelatedItem {
  readonly id: string;
  readonly quantity?: number;
}

export type RelatedEntity = API.WorkflowEntity;

type WorkflowTemplateRef = NonNullable<
  NonNullable<API.ListWorkflowEntitiesQuery['list']['refs']>['workflowTemplateRefs']
>[number];

export class RelatedEntityField implements EditableField {
  readonly items: RelatedItem[];
  readonly relatedEntities: RelatedEntity[];
  readonly workflowTemplateRefs: WorkflowTemplateRef[] | null;
  readonly fieldType = 'RelatedEntityField';

  constructor(
    items: RelatedItem[],
    entityRefs?: RelatedEntity[] | null,
    templateRefs?: WorkflowTemplateRef[] | null,
  ) {
    this.items = items;
    const itemIds = this.items.map(item => item.id);
    this.relatedEntities = entityRefs?.filter(entity => itemIds.includes(entity.id)) ?? [];
    this.workflowTemplateRefs = templateRefs ?? null;
  }

  validate(): string | null {
    return null;
  }

  isEqual(field: Field): boolean {
    return field instanceof RelatedEntityField && isEqual(this.items, field.items);
  }

  toFieldJson() {
    return this.items;
  }

  toDisplayString(): string {
    return this.relatedEntities
      .map(entity =>
        getEntityTitle(
          entity,
          this.workflowTemplateRefs?.find(template => template.id === entity.workflowTemplateId),
        ),
      )
      .filter(notNil)
      .join(', ');
  }

  get isEmpty() {
    return isEmpty(this.items);
  }

  static fromFieldJson(
    jsonBlob: any,
    entityRefs?: RelatedEntity[] | null,
    templateRefs?: WorkflowTemplateRef[] | null,
  ): RelatedEntityField | null {
    if (Array.isArray(jsonBlob) && jsonBlob.every(isRelatedItem)) {
      return new RelatedEntityField(jsonBlob, entityRefs, templateRefs);
    } else {
      return null;
    }
  }
}

function isRelatedItem(item: any): item is RelatedItem {
  return !isNil(item) && isString(item.id);
}

export function useRelatedEntities(
  field: RelatedEntityField | null,
  workflowTemplateId: string | null,
) {
  const missingEntities = useMemo(() => {
    if (isNil(field)) {
      return [];
    }
    return field.items.filter(item => !field.relatedEntities.some(entity => entity.id === item.id));
  }, [field]);

  const {
    workflowEntities,
    workflowTemplate: fetchedWorkflowTemplate,
    loading,
  } = useWorkflowEntitiesByIds({
    workflowTemplateId,
    workflowEntityIds: missingEntities.map(item => item.id),
  });

  const entities = useMemo(
    () => [...(field?.relatedEntities ?? []), ...(workflowEntities ?? [])],
    [field, workflowEntities],
  );

  const workflowTemplate =
    field?.workflowTemplateRefs?.find(template => template.id === workflowTemplateId) ??
    fetchedWorkflowTemplate;

  return {
    loading,
    entities,
    workflowTemplate,
  };
}
